import { useCallback, useMemo, useRef, useState } from 'react';
import { InfoOutlined } from '@mui/icons-material';
import {
	ConfirmModal,
	FormActions,
	FormCard,
	PageHeader,
	useConfirmModal,
	useToast,
} from '@practium/ui';
import type {
	RecommendationPageMode,
	MaintenanceFormData,
	FertilizationPlanAdvice,
} from '../types';
import { MaintenanceForm } from '../components/Forms/MaintenanceForm/MaintenanceForm';
import type { MaintenanceFormRef } from '../components/Forms/MaintenanceForm/MaintenanceForm';
import { MAINTENANCE_TITLES, MAINTENANCE_SUBTITLES } from '../constants/maintenance.constants';
import { useUpdateAdviceMaintenance } from '../../api/hooks/maintenance';
import { mapMaintenanceAdviceToForm } from '../utils/maintenanceAdviceMapper';
import { PanelActions } from '../components/PanelActions/PanelActions';
import styles from './MaintenanceAdvicePage.module.css';

export interface MaintenanceAdvicePageProps {
	onBack: () => void;
	initialMode?: RecommendationPageMode;
	initialData?: MaintenanceFormData;
	viewData?: FertilizationPlanAdvice;
	adviceId?: string;
	onDelete?: () => Promise<void> | void;
	onStart?: (data: MaintenanceFormData) => Promise<void> | void;
}

export const MaintenanceAdvicePage = ({
	onBack,
	initialMode = 'add',
	initialData,
	viewData,
	adviceId,
	onDelete,
	onStart,
}: MaintenanceAdvicePageProps) => {
	const [isLoading, setIsLoading] = useState(false);
	const formRef = useRef<MaintenanceFormRef>(null);
	const { mutateAsync: updateMaintenanceAdvice, isPending: isUpdating } =
		useUpdateAdviceMaintenance();

	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel: handleConfirmCancel,
	} = useConfirmModal();

	const { success: toastSuccess, error: toastError } = useToast();

	const handleSubmit = useCallback(
		async (data: MaintenanceFormData) => {
			if (initialMode === 'edit' && adviceId) {
				await updateMaintenanceAdvice({
					adviceId,
					name: data.name.trim(),
				});
				toastSuccess('Identificação salva com sucesso!');
				onBack();
			} else {
				await onStart?.(data);
			}
		},
		[initialMode, adviceId, updateMaintenanceAdvice, toastSuccess, onBack, onStart]
	);

	const handleCancel = useCallback(async () => {
		if (!formRef.current?.isDirty?.()) {
			onBack();
			return;
		}
		const confirmed = await confirm({
			title: 'Cancelar alterações',
			message: 'Deseja descartar as alterações não salvas?',
			confirmText: 'Descartar',
			cancelText: 'Continuar editando',
			variant: 'warning',
		});
		if (confirmed) onBack();
	}, [confirm, onBack]);

	const handleDelete = useCallback(async () => {
		const confirmed = await confirm({
			title: 'Excluir recomendação',
			message: 'Tem certeza que deseja excluir esta recomendação? Esta ação não pode ser desfeita.',
			confirmText: 'Excluir',
			cancelText: 'Cancelar',
			variant: 'danger',
		});
		if (confirmed) {
			try {
				await onDelete?.();
				toastSuccess('Recomendação excluída com sucesso.');
			} catch (error) {
				toastError(error instanceof Error ? error.message : 'Erro ao excluir recomendação.');
			}
		}
	}, [confirm, onDelete, toastError, toastSuccess]);

	const handleSave = useCallback(() => {
		formRef.current?.submit();
	}, []);

	const editInitialData: MaintenanceFormData | undefined = useMemo(() => {
		if (initialMode === 'edit' && viewData) {
			return mapMaintenanceAdviceToForm(viewData);
		}
		return initialData;
	}, [initialMode, viewData, initialData]);

	const saveText = initialMode === 'add' ? 'Iniciar' : 'Salvar';

	return (
		<div className={styles.page}>
			<div className={styles.contentWrapper}>
				<PageHeader
					title={MAINTENANCE_TITLES[initialMode]}
					subtitle={MAINTENANCE_SUBTITLES[initialMode]}
					onBack={onBack}
				/>
				<div className={styles.cards}>
					<div className={styles.mainContent}>
						<FormCard
							icon={<InfoOutlined />}
							title="Informações Gerais"
							className={styles.mainCard}
						>
							<MaintenanceForm
								key={initialMode}
								ref={formRef}
								mode={initialMode}
								initialData={editInitialData}
								viewData={viewData}
								onSubmit={handleSubmit}
								onLoadingChange={setIsLoading}
							/>
						</FormCard>
						<PanelActions>
							<FormActions
								mode={initialMode}
								onCancel={handleCancel}
								onSave={handleSave}
								isLoading={isLoading || isUpdating}
								onDelete={onDelete ? handleDelete : undefined}
								saveText={saveText}
							/>
						</PanelActions>
					</div>
				</div>
			</div>

			<ConfirmModal
				isOpen={isConfirmOpen}
				title={confirmOptions.title}
				message={confirmOptions.message}
				confirmText={confirmOptions.confirmText}
				cancelText={confirmOptions.cancelText}
				variant={confirmOptions.variant}
				onConfirm={handleConfirm}
				onCancel={handleConfirmCancel}
			/>
		</div>
	);
};
